'use client'

import { useEffect, useRef, useState } from 'react'
import { WINDOW_REGISTRY } from '@/windows/window-registry'

const DEFAULT_PINNED = [
  'features',
  'chat-info',
  'pricing',
  'price-chart',
  'chatbox',
  'domain-management',
  'user-dashboard',
  'windows-explorer',
]

const QUICK_LINKS = [
  { href: '/home', label: 'صفحه اصلی' },
  { href: '/pathway', label: 'Pathway' },
  { href: '/price-chart', label: 'قیمت‌نما' },
]

export function AtirameLogoMenu() {
  const [pinnedWindows, setPinnedWindows] = useState<string[]>(DEFAULT_PINNED)
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let cancelled = false
    const loadPins = async () => {
      try {
        const response = await fetch('/api/user-settings')
        if (!response.ok) return
        const result = await response.json()
        const pins = result?.settings?.pinnedWindows
        if (!cancelled && Array.isArray(pins) && pins.length) {
          const normalized = pins
            .filter((id: unknown): id is string => typeof id === 'string')
            .map((id) => (id === 'domain-conversations' ? 'chat-info' : id))
            .filter((id, index, arr) => arr.indexOf(id) === index)
          setPinnedWindows(normalized)
        }
      } catch {
        /* keep defaults */
      }
    }
    void loadPins()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const pinnedEntries = pinnedWindows
    .map((id) =>
      WINDOW_REGISTRY.find((window) => window.id === id && window.active !== false)
    )
    .filter(
      (window): window is (typeof WINDOW_REGISTRY)[number] => Boolean(window)
    )

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-10 w-10 items-center justify-center rounded-[var(--mafia-radius)] border border-[var(--mafia-border)] bg-black/25 p-0.5 transition hover:border-[var(--mafia-border-strong)] hover:bg-black/40"
        aria-label="پنجره‌های پین‌شده Pathway"
        title="پنجره‌های پین‌شده Pathway"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <img
          src="/robot-icon.png"
          alt="آتیرام"
          width={40}
          height={40}
          className="h-9 w-9 rounded-md object-contain"
        />
      </button>

      {open ? (
        <div
          role="menu"
          className="mafia-panel absolute left-0 top-full z-50 mt-2 w-64 space-y-2 p-2"
        >
          <div className="space-y-1 border-b border-[var(--mafia-border)] pb-2">
            <div className="px-2 py-1 text-xs text-[var(--mafia-muted)]">
              میانبرها
            </div>
            {QUICK_LINKS.map((link) => (
              <button
                key={link.href}
                type="button"
                role="menuitem"
                onClick={() => {
                  window.location.href = link.href
                }}
                className="flex w-full items-center rounded-lg px-3 py-2 text-sm text-[var(--mafia-ink)] transition hover:bg-black/30"
              >
                {link.label}
              </button>
            ))}
          </div>

          <div className="px-2 py-1 text-xs text-[var(--mafia-muted)]">
            پنجره‌های پین‌شده
          </div>
          {pinnedEntries.length ? (
            <div className="max-h-72 space-y-1 overflow-y-auto">
              {pinnedEntries.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  role="menuitem"
                  title={`رفتن به «${entry.title}» در Pathway`}
                  onClick={() => {
                    window.location.href = `/pathway?window=${encodeURIComponent(entry.id)}`
                  }}
                  className="flex w-full items-center rounded-lg px-3 py-2 text-sm text-[var(--mafia-ink)] transition hover:bg-black/30"
                >
                  {entry.title}
                </button>
              ))}
            </div>
          ) : (
            <div className="px-3 py-4 text-sm text-[var(--mafia-muted)]">
              هنوز پنجره‌ای پین نشده است.
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}
